import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { ColDef, ExcelStyle } from 'ag-grid-enterprise'
import { CustomCellRendererProps } from 'ag-grid-react'
import { sessionContractUserSelector } from '../../../../redux/session/contract/selectors'
import { Contract, ContractProduct } from '../../../../types/contract/Contract'
import CustomAgGrid from '../../../../shared/components/custom/Grid/CustomAgGrid'

interface SuperUserContractTableProps {
  contracts: Contract[]
}

const SuperUserContractTable: React.FC<SuperUserContractTableProps> = ({ contracts }) => {
  const { t } = useTranslation()
  const contentEl = useRef(null)
  const sessionContractUsers = useSelector(sessionContractUserSelector)

  const defaultColDef: ColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 100,
      resizable: true
    }),
    []
  )

  const columnDefs: ColDef<Contract>[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        filter: 'agNumberColumnFilter'
      },
      {
        field: 'parentId',
        headerName: 'Parent ID',
        filter: 'agNumberColumnFilter',
        cellRenderer: (props: CustomCellRendererProps<Contract>) => {
          if (!props.value) return null
          return <Link to={`/superuser/contracts/${props.value}`}>{props.value}</Link>
        }
      },
      {
        field: 'companyId',
        headerName: 'Y-tunnus',
        filter: 'agTextColumnFilter',
        cellClass: 'stringType'
      },
      {
        field: 'name',
        headerName: 'Nimi',
        filter: 'agTextColumnFilter'
      },
      {
        field: 'type',
        headerName: 'Tyyppi',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [...new Set(contracts.map(c => c.type))]
        }
      },
      {
        field: 'product',
        headerName: 'Tuote',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: Object.keys(ContractProduct)
        },
        valueFormatter: params => t(`contractProduct:${params.value}`)
      },
      {
        field: 'sellerId',
        headerName: 'Myyjä',
        filter: 'agSetColumnFilter',
        valueGetter: (params: any) => {
          const user = sessionContractUsers.find(s => s.id === params?.data?.sellerId)
          return user?.displayName || ''
        }
      },
      {
        field: 'price',
        headerName: 'Hinta',
        filter: 'agNumberColumnFilter',
        cellClass: 'currencyType' // Lisätty valuutta formatointia varten
      },
      {
        field: 'layout',
        headerName: 'Layout',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [...new Set(contracts.map(c => c.layout))]
        }
      },
      {
        field: 'franchise',
        headerName: 'Franchise',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [...new Set(contracts.map(c => c.franchise))]
        }
      },
      {
        field: 'domain',
        headerName: 'Domain',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [...new Set(contracts.map(c => c.domain))]
        }
      },
      {
        field: 'date',
        headerName: 'Luotu'
      },
      {
        field: 'billingStarted',
        headerName: 'Laskutus aloitettu',
        hide: true
      },
      {
        headerName: 'Toiminnot',
        colId: 'actions',
        sortable: false,
        filter: false,
        pinned: 'right',
        width: 50,
        suppressExport: true,
        cellRenderer: (props: CustomCellRendererProps<Contract>) => {
          if (!props?.data?.id) return null
          return <Link to={`/superuser/contracts/${props.data.id}`}>Avaa</Link>
        }
      }
    ],
    [contracts, sessionContractUsers, t]
  )

  const excelStyles: ExcelStyle[] = useMemo(
    () => [
      {
        id: 'stringType',
        dataType: 'String'
      }
    ],
    []
  )

  const paginationPageSizeSelector = useMemo<number[] | boolean>(() => {
    return [10, 20, 50, 100]
  }, [])

  const exportParams = useMemo(() => {
    return {
      allColumns: true,
      columnKeys: [
        'id',
        'parentId',
        'companyId',
        'name',
        'type',
        'product',
        'sellerId',
        'price',
        'layout',
        'franchise',
        'domain',
        'date',
        'billingStarted'
      ]
    }
  }, [])

  return (
    <div ref={contentEl} style={{ height: '100%', width: '100%' }}>
      <CustomAgGrid
        defaultExcelExportParams={exportParams}
        defaultCsvExportParams={exportParams}
        rowData={contracts}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        excelStyles={excelStyles}
        enableCellTextSelection
        domLayout="autoHeight"
        rowSelection="multiple"
        animateRows
        pagination
        paginationPageSize={20}
        paginationPageSizeSelector={paginationPageSizeSelector}
      />
    </div>
  )
}

export default SuperUserContractTable
